import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Shield,
  Eye,
  EyeOff,
  Search,
  Filter,
  UserCheck,
  UserX,
  Settings
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { authManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  
  const { toast } = useToast();
  const currentUser = authManager.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'Operator',
    status: 'Active',
    permissions: {
      orders: {
        view: true,
        add: false,
        edit: false,
        delete: false,
        export: false
      },
      brandSpecs: {
        view: true,
        add: false,
        edit: false,
        delete: false,
        download: false
      },
      dispatches: {
        view: true,
        add: false,
        edit: false,
        delete: false
      },
      reports: {
        view: false,
        generate: false
      }
    }
  });

  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can access user management.',
        variant: 'destructive'
      });
      return;
    }
    loadUsers();
  }, [isAdmin]);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, roleFilter, statusFilter]);

  const loadUsers = () => {
    try {
      const allUsers = authManager.getAllUsers();
      setUsers(allUsers);
      setLoading(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load users. Please try again.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query)
      );
    }

    // Role filter
    if (roleFilter !== 'All') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      role: 'Operator',
      status: 'Active',
      permissions: {
        orders: {
          view: true,
          add: false,
          edit: false,
          delete: false,
          export: false
        },
        brandSpecs: {
          view: true,
          add: false,
          edit: false,
          delete: false,
          download: false
        },
        dispatches: {
          view: true,
          add: false,
          edit: false,
          delete: false
        },
        reports: {
          view: false,
          generate: false
        }
      }
    });
    setEditingUser(null);
  };

  const handleAddUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.username.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name, email, and username are required.',
        variant: 'destructive'
      });
      return;
    }

    if (!editingUser && (!userForm.password || userForm.password !== userForm.confirmPassword)) {
      toast({
        title: 'Validation Error',
        description: 'Password and confirm password must match.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const userData = {
        ...userForm,
        id: editingUser ? editingUser.id : Date.now(),
        createdAt: editingUser ? editingUser.createdAt : new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        createdBy: currentUser?.name || 'System'
      };

      let result;
      if (editingUser) {
        result = authManager.updateUser(editingUser.id, userData);
      } else {
        result = authManager.addUser(userData);
      }

      if (result.success) {
        toast({
          title: editingUser ? 'User Updated' : 'User Added',
          description: `User ${userForm.name} ${editingUser ? 'updated' : 'added'} successfully.`,
        });
        loadUsers();
        resetUserForm();
        setShowAddUser(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || `Failed to ${editingUser ? 'update' : 'add'} user. Please try again.`,
        variant: 'destructive'
      });
    }
  };

  const handleEditUser = (user) => {
    setUserForm({
      ...user,
      password: '',
      confirmPassword: ''
    });
    setEditingUser(user);
    setShowAddUser(true);
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      toast({
        title: 'Error',
        description: 'You cannot delete your own account.',
        variant: 'destructive'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const result = authManager.deleteUser(userId);
        if (result.success) {
          toast({
            title: 'User Deleted',
            description: 'User has been deleted successfully.',
          });
          loadUsers();
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete user. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleResetPassword = () => {
    if (!resetPasswordForm.newPassword || resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast({
        title: 'Validation Error',
        description: 'New password and confirm password must match.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = authManager.resetPassword(resetPasswordUser.id, resetPasswordForm.newPassword);
      if (result.success) {
        toast({
          title: 'Password Reset',
          description: `Password for ${resetPasswordUser.name} has been reset successfully.`,
        });
        setResetPasswordForm({ newPassword: '', confirmPassword: '' });
        setShowResetPassword(false);
        setResetPasswordUser(null);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset password. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleRoleChange = (role) => {
    let permissions = { ...userForm.permissions };
    
    // Set default permissions based on role
    switch (role) {
      case 'Admin':
        permissions = {
          orders: { view: true, add: true, edit: true, delete: true, export: true },
          brandSpecs: { view: true, add: true, edit: true, delete: true, download: true },
          dispatches: { view: true, add: true, edit: true, delete: true },
          reports: { view: true, generate: true }
        };
        break;
      case 'Manager':
        permissions = {
          orders: { view: true, add: true, edit: true, delete: false, export: true },
          brandSpecs: { view: true, add: true, edit: true, delete: false, download: true },
          dispatches: { view: true, add: true, edit: true, delete: false },
          reports: { view: true, generate: true }
        };
        break;
      case 'Operator':
        permissions = {
          orders: { view: true, add: true, edit: false, delete: false, export: false },
          brandSpecs: { view: true, add: false, edit: false, delete: false, download: false },
          dispatches: { view: true, add: true, edit: false, delete: false },
          reports: { view: false, generate: false }
        };
        break;
      default:
        break;
    }
    
    setUserForm(prev => ({ ...prev, role, permissions }));
  };

  const updatePermission = (section, permission, value) => {
    setUserForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [section]: {
          ...prev.permissions[section],
          [permission]: value
        }
      }
    }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'Manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Operator': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    return status === 'Active' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access user management.</p>
          <Link to="/">
            <Button variant="outline">Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
            <p className="text-gray-600">
              {filteredUsers.length} of {users.length} users
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
          <Button 
            onClick={() => setShowAddUser(true)}
            className="bg-blue-600 hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name, email, or username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Roles</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Operator">Operator</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Users List
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first user'}
                </p>
                <Button 
                  onClick={() => setShowAddUser(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{user.username}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role)}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(user.status)}>
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setResetPasswordUser(user);
                                setShowResetPassword(true);
                              }}
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit User Dialog */}
        <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={userForm.name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={userForm.username}
                    onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Enter username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={userForm.role} onValueChange={handleRoleChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Password Fields */}
              {!editingUser && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={userForm.password}
                        onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={userForm.confirmPassword}
                        onChange={(e) => setUserForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={userForm.status} onValueChange={(value) => setUserForm(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Permissions</Label>
                
                {/* Orders Permissions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Orders Management</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(userForm.permissions.orders).map(([permission, value]) => (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={`orders-${permission}`}
                          checked={value}
                          onCheckedChange={(checked) => updatePermission('orders', permission, checked)}
                        />
                        <Label htmlFor={`orders-${permission}`} className="text-sm capitalize">
                          {permission}
                        </Label>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Brand Specs Permissions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Brand Specifications</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(userForm.permissions.brandSpecs).map(([permission, value]) => (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={`brandSpecs-${permission}`}
                          checked={value}
                          onCheckedChange={(checked) => updatePermission('brandSpecs', permission, checked)}
                        />
                        <Label htmlFor={`brandSpecs-${permission}`} className="text-sm capitalize">
                          {permission}
                        </Label>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Dispatches Permissions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Dispatch Management</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(userForm.permissions.dispatches).map(([permission, value]) => (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={`dispatches-${permission}`}
                          checked={value}
                          onCheckedChange={(checked) => updatePermission('dispatches', permission, checked)}
                        />
                        <Label htmlFor={`dispatches-${permission}`} className="text-sm capitalize">
                          {permission}
                        </Label>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Reports Permissions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Reports</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-3">
                    {Object.entries(userForm.permissions.reports).map(([permission, value]) => (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={`reports-${permission}`}
                          checked={value}
                          onCheckedChange={(checked) => updatePermission('reports', permission, checked)}
                        />
                        <Label htmlFor={`reports-${permission}`} className="text-sm capitalize">
                          {permission}
                        </Label>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleAddUser}
                  className="flex-1"
                >
                  {editingUser ? 'Update User' : 'Add User'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddUser(false);
                    resetUserForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Reset password for <strong>{resetPasswordUser?.name}</strong>
              </p>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                <Input
                  id="confirmNewPassword"
                  type="password"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleResetPassword}
                  className="flex-1"
                >
                  Reset Password
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetPasswordUser(null);
                    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserManagement;